import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStudy } from '../context/StudyContext';
import { apiService } from '../services/api';

const CustomStudySession = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { topics, isLoading } = useStudy();
  
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [sessionLength, setSessionLength] = useState(10);
  const [masteryFilters, setMasteryFilters] = useState(['notAnswered']);
  const [availableQuestions, setAvailableQuestions] = useState(0);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(true);

  useEffect(() => {
    const initialSelectedTopics = location.state?.selectedTopics || [];
    setSelectedTopics(initialSelectedTopics);
    fetchProgressData();
  }, [location.state]);

  const fetchProgressData = async () => {
    setLoadingProgress(true);
    try {
      const data = await apiService.getProgress('all');
      console.log('Progress data for custom study:', data);
      setProgressData(data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
      setProgressData({
        answered: 0,
        mastery_percentage: 0,
        mastery_breakdown: {
          almost_there: 0,
          needs_work: 0,
          mastered: 0
        },
        completion_percentage: 0
      });
    } finally {
      setLoadingProgress(false);
    }
  };

  const totalQuestions = topics.reduce((sum, topic) => {
    return sum + (topic?.questions || 0);
  }, 0);

  const totalAnswered = progressData?.answered || 0;
  
  const calculateMasteryCounts = () => {
    const masteryBreakdown = progressData?.mastery_breakdown || {
      mastered: 0,
      almost_there: 0,
      needs_work: 0
    };

    const notAnswered = totalQuestions - totalAnswered;
    const needsWork = masteryBreakdown.needs_work || 0;
    const almostThere = masteryBreakdown.almost_there || 0;
    const mastered = masteryBreakdown.mastered || 0;

    return { 
      notAnswered, 
      needsWork, 
      almostThere, 
      mastered 
    };
  };

  const masteryCounts = calculateMasteryCounts();

  useEffect(() => {
    const estimateAvailableQuestions = () => {
      if (selectedTopics.length === 0) {
        setAvailableQuestions(0);
        return;
      }
      
      const totalFromTopics = selectedTopics.reduce((total, topicId) => {
        const topic = topics.find(t => t.id === topicId);
        return total + (topic?.questions || 0);
      }, 0);
      
      setAvailableQuestions(totalFromTopics);
      
      if (totalFromTopics > 0 && sessionLength > totalFromTopics) {
        setSessionLength(Math.min(10, totalFromTopics));
      }
    };

    estimateAvailableQuestions();
  }, [selectedTopics, topics, sessionLength]);

  useEffect(() => {
    if (availableQuestions > 0 && sessionLength > availableQuestions) {
      setSessionLength(availableQuestions);
    }
  }, [availableQuestions, sessionLength]);

  const toggleTopic = (topicId) => {
    if (selectedTopics.includes(topicId)) {
      setSelectedTopics(selectedTopics.filter(id => id !== topicId));
    } else {
      setSelectedTopics([...selectedTopics, topicId]);
    }
  };

  const toggleMasteryFilter = (mastery) => {
    if (masteryFilters.includes(mastery)) {
      setMasteryFilters(masteryFilters.filter(m => m !== mastery));
    } else {
      setMasteryFilters([...masteryFilters, mastery]);
    }
  };

  const handleSessionLengthChange = (value) => {
    setSessionLength(value);
  };

  const fetchQuestionsForSession = async (sessionId) => {
    if (!selectedTopics || selectedTopics.length === 0) {
      throw new Error('No topics selected');
    }

    console.log(`Fetching questions for ${sessionLength} questions from topics:`, selectedTopics);
    console.log('Mastery filters:', masteryFilters);

    const questions = [];
    let attempts = 0;
    const maxAttempts = sessionLength * 3; 

    const selectedApiTopics = selectedTopics.map(topicId => {
      const topic = topics.find(t => t.id === topicId);
      return topic?.api_topic;
    }).filter(Boolean);

    if (selectedApiTopics.length === 0) {
      throw new Error('No valid topics selected');
    }

    while (questions.length < sessionLength && attempts < maxAttempts) {
      attempts++;
      
      const currentTopicIndex = questions.length % selectedApiTopics.length;
      const currentTopic = selectedApiTopics[currentTopicIndex];

      try {
        const question = await apiService.getNextQuestion(
          currentTopic,
          'medium', 
          sessionId
        );

        if (question && question.id) {
          const isDuplicate = questions.some(q => q.id === question.id);
          if (!isDuplicate) {
            questions.push(question);
            console.log(`Added question ${questions.length}:`, question.id);
          }
        }
      } catch (error) {
        console.error(`Error fetching question from topic ${currentTopic}:`, error);
      }

      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (questions.length === 0) {
      throw new Error('Could not fetch any questions for the selected topics');
    }

    console.log(`Successfully fetched ${questions.length} questions`);
    return questions;
  };

  const startSession = async () => {
    setLoadingQuestions(true);
    try {
      const sessionResponse = await apiService.startSession();
      const newSessionId = sessionResponse.session_id;
      
      console.log('New session created:', newSessionId);

      const sessionQuestions = await fetchQuestionsForSession(newSessionId);
      
      if (sessionQuestions && sessionQuestions.length > 0) {
        navigate('/question', { 
          state: { 
            questions: sessionQuestions, 
            sessionLength: sessionQuestions.length,
            selectedTopics,
            masteryFilters,
            sessionId: newSessionId
          } 
        });
      } else {
        throw new Error('Could not fetch questions for session');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again. Error: ' + error.message);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const selectAllTopics = () => {
    setSelectedTopics(topics.map(topic => topic.id));
  };

  const clearTopics = () => {
    setSelectedTopics([]);
  };

  const selectAllMastery = () => {
    setMasteryFilters(['notAnswered', 'needsWork', 'almostThere', 'mastered']);
  };

  const clearMastery = () => {
    setMasteryFilters([]);
  };

  const estimatedTime = Math.round(sessionLength * 1.2);

  const getMasteryInfo = (mastery) => {
    const info = {
      notAnswered: { name: 'Not answered', color: '#999999' },
      needsWork: { name: 'Needs Work', color: '#dc3545'},
      almostThere: { name: 'Almost There', color: '#ffc107'},
      mastered: { name: 'Mastered', color: '#28a745'}
    };
    return info[mastery] || info.notAnswered;
  };

  if (isLoading || loadingProgress) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading study materials...</p>
      </div>
    );
  }

  return (
    <div style={styles.customStudyModal}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdn.materialdesignicons.com/7.4.47/css/materialdesignicons.min.css" rel="stylesheet" />
      
      <div style={styles.closeButtonContainer}>
        <button style={styles.iconButton} type="button" onClick={() => navigate(-1)}>
          <span className="mdi mdi-close" style={styles.closeIcon}></span>
        </button>
      </div>

      <h1 style={styles.modalTitle}>Custom Study Session</h1>

      <div style={styles.modalContent}>
        {loadingQuestions && (
          <div style={styles.loadingAlert}>
            <div style={styles.smallSpinner}></div>
            Loading questions...
          </div>
        )}

        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Learning Mode</div>
          </div>
          
          <div style={styles.learningModeOption}>
            <div style={styles.optionContent}>
              <div style={styles.optionLeft}>
                <div style={styles.radioContainer}>
                  <div 
                    style={{
                      ...styles.radioButton,
                      borderColor: '#2c5aa0',
                      backgroundColor: '#2c5aa0'
                    }}
                    role="radio"
                    aria-checked="true"
                    aria-label="Questions"
                  >
                    <div style={styles.radioInner}></div>
                  </div>
                </div>
                <div style={styles.optionText}>Questions</div>
              </div>
              <div style={styles.optionChip}>
                <span style={styles.chipText}>{availableQuestions}</span>
              </div>
            </div>
          </div>

          <div style={styles.sessionLengthSection}>
            <div style={styles.sessionLengthHeader}>
              <div style={styles.sessionLengthTitle}>Session Length</div>
              <div style={styles.estimatedTime}>~{estimatedTime} minutes</div>
            </div>
            
            <div style={styles.sliderContainer}>
              <div style={styles.sliderWrapper}>
                <input
                  type="range"
                  min="5"
                  max={Math.min(50, availableQuestions)}
                  step="5"
                  value={sessionLength}
                  onChange={(e) => handleSessionLengthChange(parseInt(e.target.value))}
                  style={styles.sessionSlider}
                  disabled={availableQuestions === 0}
                  className="session-slider"
                />
                <div style={styles.sliderTrack}>
                  <div 
                    style={{
                      ...styles.sliderFill,
                      width: `${(sessionLength / Math.min(50, availableQuestions)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
              <div style={styles.sessionLengthChip}>
                <span style={styles.chipText}>{sessionLength} / {Math.min(50, availableQuestions)} items</span>
              </div>
            </div>
            
            <div style={styles.availableInfo}>
              <div style={styles.availableText}>{availableQuestions} available</div>
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Mastery</div>
            <div style={styles.masteryActions}>
              <button style={styles.smallButton} onClick={selectAllMastery} className="small-button">All</button>
              <button style={styles.smallButton} onClick={clearMastery} className="small-button">Clear</button>
            </div>
          </div>
          
          {['notAnswered', 'needsWork', 'almostThere', 'mastered'].map((mastery) => {
            const masteryInfo = getMasteryInfo(mastery);
            const isSelected = masteryFilters.includes(mastery);
            const count = masteryCounts[mastery];
            
            return (
              <div 
                key={mastery}
                style={{
                  ...styles.masteryOption,
                  ...(isSelected ? styles.masteryOptionSelected : {})
                }}
                onClick={() => toggleMasteryFilter(mastery)}
                className="mastery-option"
              >
                <div style={styles.optionContent}>
                  <div style={styles.optionLeft}>
                    <div style={styles.checkboxContainer}>
                      <div 
                        style={{
                          ...styles.checkbox,
                          ...(isSelected ? styles.checkboxChecked : {}),
                          border: isSelected ? '2px solid #2c5aa0' : '2px solid #666666',
                          backgroundColor: isSelected ? '#2c5aa0' : 'transparent'
                        }}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={masteryInfo.name}
                      >
                        {isSelected && (
                          <span className="mdi mdi-check" style={styles.checkboxIcon}></span>
                        )}
                      </div>
                    </div>
                    <span className={`mdi ${masteryInfo.icon}`} style={{
                      ...styles.masteryOptionIcon,
                      color: masteryInfo.color
                    }}></span>
                    <div style={styles.optionText}>{masteryInfo.name}</div>
                  </div>
                  <div 
                    style={{
                      ...styles.masteryChip,
                      backgroundColor: masteryInfo.color
                    }}
                  >
                    <span style={styles.masteryChipText}>{count} items</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Knowledge Levels</div>
            <div style={styles.topicActions}>
              <button style={styles.smallButton} onClick={selectAllTopics} className="small-button">All</button>
              <button style={styles.smallButton} onClick={clearTopics} className="small-button">Clear</button>
            </div>
          </div>
          
          {topics.map((topic) => {
            const isSelected = selectedTopics.includes(topic.id);
            
            return (
              <div 
                key={topic.id}
                style={{
                  ...styles.topicOption,
                  ...(isSelected ? styles.topicOptionSelected : {})
                }}
                onClick={() => toggleTopic(topic.id)}
                className="topic-option"
              >
                <div style={styles.optionContent}>
                  <div style={styles.optionLeft}>
                    <div style={styles.checkboxContainer}>
                      <div 
                        style={{
                          ...styles.checkbox,
                          ...(isSelected ? styles.checkboxChecked : {}),
                          border: isSelected ? '2px solid #2c5aa0' : '2px solid #666666',
                          backgroundColor: isSelected ? '#2c5aa0' : 'transparent'
                        }}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={topic.name}
                      >
                        {isSelected && (
                          <span className="mdi mdi-check" style={styles.checkboxIcon}></span>
                        )}
                      </div>
                    </div>
                    <div style={styles.optionText}>{topic.name}</div>
                  </div>
                  <div style={styles.topicChip}>
                    <span style={styles.topicChipText}>{topic.questions || 0} items</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={styles.startButtonContainer}>
          <button 
            style={{
              ...styles.startButton,
              ...(availableQuestions === 0 || sessionLength === 0 || loadingQuestions || selectedTopics.length === 0 || masteryFilters.length === 0 ? styles.startButtonDisabled : {})
            }}
            onClick={startSession}
            disabled={availableQuestions === 0 || sessionLength === 0 || loadingQuestions || selectedTopics.length === 0 || masteryFilters.length === 0}
            className="start-button"
          >
            <span className="mdi mdi-play" style={styles.buttonIcon}></span>
            <div style={styles.buttonText}>
              {loadingQuestions ? 'Loading...' : availableQuestions === 0 ? 'No Questions Available' : `Start ${sessionLength} Question Session`}
            </div>
          </button>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap');
        @import url('https://cdn.materialdesignicons.com/7.4.47/css/materialdesignicons.min.css');
        
        .session-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #2c5aa0;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .session-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #2c5aa0;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .icon-button:hover {
          background-color: rgba(44, 90, 160, 0.1);
        }

        .start-button:hover:not(:disabled) {
          background-color: #1e3d6f;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(44, 90, 160, 0.3);
        }

        .small-button:hover {
          background-color: #2c5aa0;
          color: white;
          transform: translateY(-1px);
        }

        .mastery-option:hover,
        .topic-option:hover {
          background-color: #f8f9fa;
          transform: translateY(-1px);
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 480px) {
          .custom-study-modal {
            margin: 16px;
            max-height: calc(100vh - 32px);
          }
          
          .section {
            padding: 0 12px;
          }
          
          .option-text {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  customStudyModal: {
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'hidden',
    position: 'relative',
    fontFamily: '"Open Sans", sans-serif',
    margin: '20px auto',
    border: '1px solid #e0e0e0'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2c5aa0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  smallSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #f3f3f3',
    borderTop: '2px solid #2c5aa0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
  },
  loadingText: {
    marginTop: '16px',
    color: '#666666',
    fontFamily: '"Open Sans", sans-serif',
    fontWeight: '500'
  },
  loadingAlert: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #bbdefb',
    borderRadius: '6px',
    padding: '12px 16px',
    margin: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    color: '#1565c0',
    fontFamily: '"Open Sans", sans-serif',
    fontWeight: '500'
  },
  closeButtonContainer: {
    position: 'absolute',
    right: '12px',
    top: '12px',
    zIndex: 1000,
  },
  iconButton: {
    backgroundColor: 'transparent',
    width: '40px',
    height: '40px',
    border: 'none',
    borderRadius: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
    fontFamily: '"Open Sans", sans-serif',
  },
  closeIcon: {
    fontSize: '24px',
    color: '#666666',
    fontFamily: 'material-community',
    fontWeight: 'normal',
    fontStyle: 'normal',
  },
  modalTitle: {
    direction: 'ltr',
    color: '#2c5aa0',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0px',
    fontWeight: 600,
    lineHeight: '32px',
    fontSize: '24px',
    textAlign: 'center',
    margin: '24px 0',
    padding: '0 16px',
  },
  modalContent: {
    flex: 1,
    paddingBottom: '0px',
    minHeight: '440px',
    overflowY: 'auto',
    maxHeight: 'calc(90vh - 100px)',
    fontFamily: '"Open Sans", sans-serif',
  },
  section: {
    padding: '0 20px',
    marginBottom: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  sectionHeader: {
    padding: '18px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontFamily: '"Open Sans", sans-serif',
    borderBottom: '1px solid #f0f0f0'
  },
  sectionTitle: {
    direction: 'ltr',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.1px',
    fontWeight: 600,
    lineHeight: '20px',
    fontSize: '16px',
    color: '#2c5aa0',
  },
  masteryActions: {
    display: 'flex',
    gap: '8px',
    fontFamily: '"Open Sans", sans-serif',
  },
  topicActions: {
    display: 'flex',
    gap: '8px',
    fontFamily: '"Open Sans", sans-serif',
  },
  smallButton: {
    backgroundColor: 'transparent',
    border: '1px solid #2c5aa0',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    color: '#2c5aa0',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: '"Open Sans", sans-serif',
    fontWeight: '500'
  },
  learningModeOption: {
    opacity: 1,
    padding: '12px 0',
    cursor: 'pointer',
    fontFamily: '"Open Sans", sans-serif',
  },
  masteryOption: {
    opacity: 1,
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    borderRadius: '8px',
    margin: '4px 0',
    fontFamily: '"Open Sans", sans-serif',
    border: '1px solid transparent'
  },
  masteryOptionSelected: {
    opacity: 1,
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0'
  },
  topicOption: {
    opacity: 1,
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    borderRadius: '8px',
    margin: '4px 0',
    fontFamily: '"Open Sans", sans-serif',
    border: '1px solid transparent'
  },
  topicOptionSelected: {
    opacity: 1,
    backgroundColor: '#f8f9fa',
    border: '1px solid #e0e0e0'
  },
  optionContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    fontFamily: '"Open Sans", sans-serif',
  },
  optionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    fontFamily: '"Open Sans", sans-serif',
  },
  radioContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '24px',
    width: '24px',
  },
  radioButton: {
    width: '20px',
    height: '20px',
    border: '2px solid #2c5aa0',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  radioInner: {
    width: '10px',
    height: '10px',
    backgroundColor: '#FFFFFF',
    borderRadius: '50%',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '24px',
    width: '24px',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    borderRadius: '4px',
  },
  checkboxChecked: {
  },
  checkboxIcon: {
    fontSize: '16px',
    color: '#FFFFFF',
    fontFamily: 'material-community',
    fontWeight: 'bold',
  },
  optionIcon: {
    fontSize: '20px',
    color: '#333333',
    fontFamily: 'material-community',
  },
  masteryOptionIcon: {
    fontSize: '20px',
    fontFamily: 'material-community',
  },
  topicOptionIcon: {
    fontSize: '20px',
    color: '#333333',
    fontFamily: 'material-community',
  },
  optionText: {
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0px',
    fontWeight: 500,
    direction: 'ltr',
    color: '#333333',
    flex: 1,
    fontSize: '14px',
  },
  optionChip: {
    backgroundColor: '#e3f2fd',
    borderRadius: '6px',
    padding: '6px 12px',
    marginLeft: '16px',
    alignSelf: 'center',
    boxShadow: 'none',
    fontFamily: '"Open Sans", sans-serif',
  },
  chipText: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: '12px',
    color: '#2c5aa0',
    fontWeight: '600'
  },
  masteryChip: {
    borderRadius: '6px',
    padding: '6px 12px',
    marginLeft: '16px',
    alignSelf: 'center',
    boxShadow: 'none',
    minWidth: '60px',
    textAlign: 'center',
    fontFamily: '"Open Sans", sans-serif',
  },
  masteryChipText: {
    direction: 'ltr',
    color: '#FFFFFF',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.5px',
    fontWeight: 600,
    lineHeight: '16px',
    fontSize: '11px',
  },
  topicChip: {
    backgroundColor: '#e3f2fd',
    borderRadius: '6px',
    padding: '6px 12px',
    marginLeft: '16px',
    alignSelf: 'center',
    boxShadow: 'none',
    minWidth: '60px',
    textAlign: 'center',
    fontFamily: '"Open Sans", sans-serif',
  },
  topicChipText: {
    direction: 'ltr',
    color: '#2c5aa0',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.5px',
    fontWeight: 600,
    lineHeight: '16px',
    fontSize: '11px',
  },
  sessionLengthSection: {
    marginTop: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  sessionLengthHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '18px 0',
    fontFamily: '"Open Sans", sans-serif',
  },
  sessionLengthTitle: {
    direction: 'ltr',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.1px',
    fontWeight: 600,
    lineHeight: '20px',
    fontSize: '16px',
    color: '#2c5aa0',
  },
  estimatedTime: {
    direction: 'ltr',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.1px',
    fontWeight: 500,
    lineHeight: '20px',
    fontSize: '14px',
    color: '#666666',
  },
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '0 16px',
    justifyContent: 'space-between',
    fontFamily: '"Open Sans", sans-serif',
  },
  sliderWrapper: {
    flex: 1,
    position: 'relative',
    height: '4px',
  },
  sessionSlider: {
    width: '100%',
    height: '4px',
    WebkitAppearance: 'none',
    background: 'transparent',
    outline: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 2,
    cursor: 'pointer',
  },
  sliderTrack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: '#e0e0e0',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  sliderFill: {
    height: '100%',
    background: '#2c5aa0',
    borderRadius: '2px',
    transition: 'width 0.2s',
  },
  sessionLengthChip: {
    backgroundColor: '#e3f2fd',
    borderRadius: '6px',
    padding: '6px 12px',
    marginLeft: '16px',
    fontFamily: '"Open Sans", sans-serif',
  },
  availableInfo: {
    padding: '0 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    fontFamily: '"Open Sans", sans-serif',
  },
  availableText: {
    direction: 'ltr',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.5px',
    fontWeight: 500,
    lineHeight: '16px',
    fontSize: '12px',
    color: '#666666',
  },
  startButtonContainer: {
    padding: '24px 20px 20px',
    display: 'flex',
    justifyContent: 'center',
    fontFamily: '"Open Sans", sans-serif',
    borderTop: '1px solid #f0f0f0',
    marginTop: '20px'
  },
  startButton: {
    backgroundColor: '#2c5aa0',
    border: 'none',
    borderRadius: '6px',
    padding: '14px 32px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    minWidth: '240px',
    justifyContent: 'center',
    fontFamily: '"Open Sans", sans-serif',
    fontWeight: 600,
    fontSize: '16px'
  },
  startButtonDisabled: {
    backgroundColor: '#a0a8ac',
    cursor: 'not-allowed',
  },
  buttonIcon: {
    fontSize: '20px',
    color: '#ffffff',
    fontFamily: 'material-community',
  },
  buttonText: {
    direction: 'ltr',
    color: '#ffffff',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.1px',
    fontWeight: 600,
    lineHeight: '20px',
    fontSize: '16px',
  },
};

export default CustomStudySession;